import axiosInstance from "@/api/config";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { COUNTRY_NAME } from "@/constants/dataset";
import { Timezone } from "@/constants/dateTime";
import { safeBase64Encode } from "@/lib/utils";
import {
  ADMIN_AREA_LEVEL_MAP,
  AdminAreaLevelKey,
  EAdminAreaLevel
} from "@/types/adminArea";
import {
  AttributeOption,
  DatasetDetailResponse,
  DatasetFilter,
  DatasetFilterPayload,
  EAttributeType,
  EFilterOptionType,
  H3Configuration
} from "@/types/dataset";
import { AdminAreaSettings } from "@/types/spatial";
import { buildGridConfig } from "@/utils/dataset";
import { splitDoubleColon } from "@/utils/string";

import { buildFilterAsDatasetPayload } from "./filterHelper";
import { removeStartFromUnderscore } from "./string";

/**
 * Creates an admin area option object from a DatasetFilter.
 *
 * This function parses the admAreaAllSelected property to find the matching admin area by id,
 * and returns an object containing label, value, code, adm_unit, and isMatched properties.
 *
 * @param {DatasetFilter | undefined} filter - The filter object to process.
 * @returns {object | null} An object with admin area option properties, or null if filter is undefined.
 */
export const createAdminAreaOption = (filter: DatasetFilter | undefined) => {
  if (!filter || !Array.isArray(filter.admAreaAllSelected)) return null;

  const matched = filter.admAreaAllSelected
    .map((item) => splitDoubleColon(item, { id: "", code: "" }))
    .find((parsed) => parsed.id === filter.admAreaId);

  return {
    label: filter.filterLabel || "",
    value: filter.admAreaId || "",
    code: matched?.code || "",
    adm_unit: filter.admAreaUnit || "",
    isMatched: Boolean(matched)
  };
};

/**
 * Helper function to extract code from combined value::code format
 * @param combinedString - String in format "value::code"
 * @returns The code part after "::" or the original string if no "::" found
 */
const extractCodeFromCombined = (combinedString: string): string => {
  const parts = combinedString.split("::");
  return parts.length > 1 ? parts[1] : combinedString;
};

/**
 * Helper function to check if an attribute code is an admin area level (1-4)
 * @param attributeCode - The attribute code to test
 * @returns True if the attribute code matches admin area level pattern
 */
export const isAdminAreaLevel = (
  attributeCode: string | null | undefined
): boolean => {
  if (!attributeCode) return false;
  return [
    EAdminAreaLevel.LEVEL_1,
    EAdminAreaLevel.LEVEL_2,
    EAdminAreaLevel.LEVEL_3,
    EAdminAreaLevel.LEVEL_4
  ].includes(attributeCode as EAdminAreaLevel);
};

/**
 * Helper function to check if an attribute code is NOT an admin area level (1-4)
 * @param attributeCode - The attribute code to test
 * @returns True if the attribute code does NOT match admin area level pattern
 */
export const isNotAdminAreaLevel = (
  attributeCode: string | null | undefined
): boolean => {
  return !isAdminAreaLevel(attributeCode);
};

export const getDatasetAttributeType = (
  dataType: string
): EFilterOptionType | null => {
  switch (dataType) {
    case EAttributeType.ADDRESS:
    case EAttributeType.TITLE:
    case EAttributeType.TEXT:
    case EAttributeType.STRING:
    case EAttributeType.BOOLEAN:
      return EFilterOptionType.MULTIPLE;
    case EAttributeType.DATE_TIME:
      return EFilterOptionType.DATE_TIME;
    case EAttributeType.DATE:
      return EFilterOptionType.DATE;
    case EAttributeType.TIME:
      return EFilterOptionType.TIME;
    case EAttributeType.FLOAT:
    case EAttributeType.INT:
      return EFilterOptionType.MIN_MAX;
    default:
      return null;
  }
};

export const existAttributeOptionsByType = (
  filterOptions: AttributeOption[],
  selectedAttribute: AttributeOption | null,
  filterType: EFilterOptionType
): boolean => {
  if (!selectedAttribute) return false;

  return filterOptions.some(
    (item) => item.id === selectedAttribute.id && item.type === filterType
  );
};

export const generateAttributeFilterOptions = (
  selectedDataset: DatasetDetailResponse | null
): AttributeOption[] => {
  if (!selectedDataset?.schema?.length) return [];

  // Exclude geometry type from the schema
  const schemaExcludeGeomType = selectedDataset.schema?.filter(
    (item) => item.data_type !== "geometry" && item.is_visible === true
  );

  if (!schemaExcludeGeomType.length) return [];

  let filterOptionsMap: AttributeOption[] = schemaExcludeGeomType.map(
    (item) => ({
      ...item,
      id: `${item.id}_${item.position}`,
      label: item.attribute_name,
      type: getDatasetAttributeType(item.data_type),
      time_format: item?.time_format,
      value: item.id
    })
  );

  /* If dataset source is bvt, exclude the Id attribute from the dropdown, 
    if source is user dataset, then keep the Id attribute in the dropdown */
  if (selectedDataset.source === "bvt") {
    filterOptionsMap = filterOptionsMap.filter((item) => item.label !== "id");
  }
  return filterOptionsMap;
};

export const mapDatasetFiltersToPayload = (
  filters: DatasetFilter[],
  timezone?: Timezone
) => {
  const uniqueId = new Set<string>();
  const filtersMap: DatasetFilterPayload[] = [];
  filters?.forEach((filter) => {
    const rawAttributeId = filter.paired_key_attribute_id ?? filter.filterId;
    const attributeId = removeStartFromUnderscore(rawAttributeId);
    const attributeCode = filter.attributeCode;
    if (uniqueId.has(attributeId)) {
      const indexOfArray = filtersMap.findIndex(
        (item) => item.id === attributeId
      );
      if (Array.isArray(filtersMap[indexOfArray].filter)) {
        filtersMap[indexOfArray].filter = Array.from(
          new Set([
            ...filtersMap[indexOfArray].filter,
            ...(filter.selectedOptions ?? [])
          ])
        );
        // If there is double identical id, remove attribute_code
        delete filtersMap[indexOfArray].attribute_code;
      }
    } else {
      uniqueId.add(attributeId);
      filtersMap.push({
        id: attributeId,
        filter: buildFilterAsDatasetPayload(filter, timezone),
        ...(attributeCode && attributeCode === "group_name_code"
          ? { attribute_code: attributeCode }
          : {})
      });
    }
  });
  return filtersMap;
};

export function getDeepestAdminFilterFirst(
  filters: DatasetFilter[]
): DatasetFilter[] {
  let highestLevelFilter: DatasetFilter | undefined;
  let highestLevel = -1;
  const otherFilters: DatasetFilter[] = [];

  for (const filter of filters) {
    if (filter.attributeCode?.includes("adm_area_level")) {
      const parts = filter.attributeCode.split("_");
      const level = Number(parts[parts.length - 1]);
      if (!isNaN(level) && level > highestLevel) {
        highestLevel = level;
        highestLevelFilter = filter;
      }
    } else {
      otherFilters.push(filter);
    }
  }

  return highestLevelFilter
    ? [highestLevelFilter, ...otherFilters]
    : otherFilters;
}

export const getAvailAggregationTypeAdminArea = (
  filterOptions: AttributeOption[],
  savedFilters: DatasetFilter[],
  dataset: DatasetDetailResponse | null
) => {
  const filteredSavedFilters = getFilteredSavedFilters(savedFilters, dataset);
  return filterOptions
    .filter((option) => {
      const isAdminArea = isAdminAreaLevel(option.attribute_code);
      const isStringType = option.data_type === "string";
      return !(isAdminArea && isStringType);
    })
    .map((option) => ({
      ...option,
      value: option.id,
      label: option.label,
      disabled: filteredSavedFilters.some(
        (filter: DatasetFilter) =>
          filter.filterId === option.id &&
          filter.attributeCode === option.attribute_code
      )
    }));
};

export const getFilteredSavedFilters = (
  savedFilters: DatasetFilter[],
  dataset: DatasetDetailResponse | null,
  showAdminAreaFilter?: boolean
) => {
  const showAdmin = showAdminAreaFilter ?? false;
  return savedFilters.filter((filter) => {
    const isAdminAreaFilterInSchema = dataset?.schema?.some(
      (schemaItem) =>
        isAdminAreaLevel(schemaItem.attribute_code) &&
        schemaItem.id === filter.attributeId
    );

    return showAdmin ? isAdminAreaFilterInSchema : !isAdminAreaFilterInSchema;
  });
};

export const getValueOfHighestLevelAdminArea = (
  data: AdminAreaSettings | null
): string | null => {
  if (!data) return null;
  // Exclude level 1 because level is country with value like 'id' and not call get admin area from it
  for (let i = 4; i >= 1; i--) {
    const levelKey = `lv${i}` as keyof AdminAreaSettings;
    if (data[levelKey]?.value) return data[levelKey]?.value ?? null;
  }
  return null;
};

/**
 * Helper function to create initial admin area settings based on dataset country
 * @param selectedDataset - The selected dataset
 * @returns Initial admin area settings with lv0 populated if valid country
 */
export const createInitialAdminArea = (
  selectedDataset: DatasetDetailResponse | null
): AdminAreaSettings => {
  const selectedCountry = selectedDataset?.country_id;
  const isValidCountry =
    selectedCountry && selectedCountry !== COUNTRY_NAME.global;

  const lv0Option = isValidCountry
    ? {
        label: COUNTRY_NAME[selectedCountry],
        value: selectedCountry,
        isMatched: false
      }
    : null;

  return {
    lv0: lv0Option,
    lv1: null,
    lv2: null,
    lv3: null,
    lv4: null
  };
};

export interface SelectedAreaSchema {
  level: AdminAreaLevelKey;
  attributeCode: string;
  schema: AttributeOption;
  selectedValue: IOptionItem;
}

/**
 * Helper function to get all selected area schemas from admin area settings
 * @param selectedAdminArea - The selected admin area settings
 * @param filterOptions - Available filter options to match against
 * @returns Array of selected area schemas
 */
export const getAllSelectedAreaSchemas = (
  selectedAdminArea: AdminAreaSettings | null,
  filterOptions: AttributeOption[]
): SelectedAreaSchema[] => {
  if (!selectedAdminArea) return [];

  const levels: readonly AdminAreaLevelKey[] = [
    "lv0",
    "lv1",
    "lv2",
    "lv3",
    "lv4"
  ] as const;
  const schemas: SelectedAreaSchema[] = [];

  for (const level of levels) {
    const selectedValue = selectedAdminArea[level];
    if (selectedValue) {
      const attributeCode = ADMIN_AREA_LEVEL_MAP[level];

      const schemaOption = filterOptions.find(
        (option) => option.attribute_code === attributeCode
      );

      if (schemaOption) {
        schemas.push({
          level,
          attributeCode,
          schema: schemaOption,
          selectedValue
        });
      }
    }
  }

  return schemas;
};

/**
 * Admin area requirement configuration
 */
export interface AdminAreaRequirement {
  lv0: boolean;
  lv1: boolean;
  lv2: boolean;
  lv3: boolean;
  lv4: boolean;
}

/**
 * Admin area visibility configuration
 */
export interface AdminAreaVisibility {
  lv0: boolean;
  lv1: boolean;
  lv2: boolean;
  lv3: boolean;
  lv4: boolean;
}

/**
 * Helper function to determine admin area requirements based on spatial aggregation type
 * @param selectedDataset - The selected dataset
 * @returns Object indicating which admin area levels are required
 */
export const getAdminAreaRequired = (
  selectedDataset: DatasetDetailResponse | null
): AdminAreaRequirement => {
  const defaultRequirement: AdminAreaRequirement = {
    lv0: false,
    lv1: false,
    lv2: false,
    lv3: false,
    lv4: false
  };

  const fallbackCondition =
    !selectedDataset?.schema || selectedDataset?.source !== "bvt";

  // return default requirement when fallback condition is met
  if (fallbackCondition) {
    return defaultRequirement;
  }

  const minAreaLevel = selectedDataset?.min_area_level_filter ?? 0;
  return {
    lv0: minAreaLevel >= 0,
    lv1: minAreaLevel >= 1,
    lv2: minAreaLevel >= 2,
    lv3: minAreaLevel >= 3,
    lv4: minAreaLevel >= 4
  };
};

/**
 * Helper function to determine admin area visibility based on dataset schema
 * @param selectedDataset - The selected dataset
 * @returns Object indicating which admin area levels are visible
 */
export const getAdminAreaVisibility = (
  selectedDataset: DatasetDetailResponse | null
): AdminAreaVisibility => {
  if (!selectedDataset?.schema) {
    return {
      lv0: false,
      lv1: false,
      lv2: false,
      lv3: false,
      lv4: false
    };
  }

  const schemaAttributeCodes = selectedDataset.schema.map(
    (item) => item.attribute_code
  );

  return {
    lv0: true,
    lv1: schemaAttributeCodes.includes(EAdminAreaLevel.LEVEL_1),
    lv2: schemaAttributeCodes.includes(EAdminAreaLevel.LEVEL_2),
    lv3: schemaAttributeCodes.includes(EAdminAreaLevel.LEVEL_3),
    lv4: schemaAttributeCodes.includes(EAdminAreaLevel.LEVEL_4)
  };
};

/**
 * Helper function to validate if admin area selection is valid
 * @param selectedAdminArea - The selected admin area settings
 * @param selectedDataset - The selected dataset
 * @returns Boolean indicating if admin area selection is valid
 */
export const isAdminAreaValid = (
  selectedAdminArea: AdminAreaSettings | null,
  selectedDataset: DatasetDetailResponse | null
): boolean => {
  if (!selectedDataset || selectedDataset?.source !== "bvt") {
    return true;
  }

  if (
    !selectedDataset?.spatial_aggregation_type ||
    (selectedDataset.spatial_aggregation_type !== "adm_area" &&
      selectedDataset.spatial_aggregation_type !== "poi" &&
      selectedDataset?.spatial_aggregation_type !== "H3")
  ) {
    return true;
  }

  if (!selectedAdminArea) return false;

  const required = getAdminAreaRequired(selectedDataset);

  return Object.entries(required).every(([level, isRequired]) => {
    if (!isRequired) return true;
    const adminAreaKey = level as keyof AdminAreaSettings;
    const adminAreaValue = selectedAdminArea[adminAreaKey];
    return adminAreaValue && adminAreaValue.value;
  });
};

/**
 * Helper function to handle admin area changes and auto-select filter options
 * @param newAdminArea - The new admin area settings
 * @param autoSelectFilter - Whether to auto-select the first available filter
 * @param filterOptions - Available filter options
 * @param savedFilters - Current saved filters
 * @returns Object with selected filter and default value if auto-selection is enabled
 */
export const handleAdminAreaChangeHelper = (
  newAdminArea: AdminAreaSettings | null,
  filterOptions: AttributeOption[],
  savedFilters: DatasetFilter[]
) => {
  const areaSchemas = getAllSelectedAreaSchemas(newAdminArea, filterOptions);
  if (areaSchemas.length > 0) {
    const adminAreas: DatasetFilter[] = [];
    const allAreaSelectedValueCode = areaSchemas
      .map(
        (areaSchema) =>
          `${areaSchema.selectedValue.value}::${areaSchema.selectedValue.code}`
      )
      .filter((item) => Boolean(item));

    const allAreaSelectedCode = areaSchemas
      .map((areaSchema) => areaSchema.selectedValue.code)
      .filter((item) => Boolean(item));

    areaSchemas.forEach((areaSchema) => {
      adminAreas.push({
        filterId: areaSchema.schema.id,
        filterLabel: areaSchema.selectedValue.label || "",
        attributeCode: areaSchema.schema.attribute_code || "",
        attributeId: removeStartFromUnderscore(areaSchema.schema.id),
        admAreaUnit: areaSchema.selectedValue.adm_unit || "",
        admAreaId: areaSchema.selectedValue.value || "",
        admAreaAllSelected: allAreaSelectedValueCode,
        filterType: EFilterOptionType.MULTIPLE,
        selectedOptions: [
          allAreaSelectedValueCode[allAreaSelectedValueCode.length - 1]
            ? extractCodeFromCombined(
                allAreaSelectedValueCode[allAreaSelectedValueCode.length - 1]
              )
            : allAreaSelectedCode[allAreaSelectedCode.length - 1] ||
              `${areaSchema.selectedValue.code}`
        ]
      });
    });
    return adminAreas;
  } else {
    return savedFilters.filter(
      (f: DatasetFilter) =>
        f.attributeCode && !f.attributeCode.includes("adm_area_level")
    );
  }
};

/**
 * Validates a dateTimeRange object to determine if it is suitable for form submission.
 *
 * This function considers the following conditions as valid:
 * - All date and time fields (`from.date`, `from.time`, `to.date`, `to.time`) are empty.
 * - Each of the `from` and `to` objects must either have both `date` and `time` filled in, or both empty.
 *
 * This is typically used to enable or disable a submit button in a form UI.
 *
 * @param {Object} dateTimeRange - The object containing from/to date and time values.
 * @param {Object} [dateTimeRange.from] - The starting date and time range.
 * @param {string} [dateTimeRange.from.date] - The start date string (optional).
 * @param {string} [dateTimeRange.from.time] - The start time string (optional).
 * @param {Object} [dateTimeRange.to] - The ending date and time range.
 * @param {string} [dateTimeRange.to.date] - The end date string (optional).
 * @param {string} [dateTimeRange.to.time] - The end time string (optional).
 *
 * @returns {boolean} `true` if the date-time range is valid for submission (i.e., complete or fully empty), otherwise `false`.
 */
export const isDateTimeRangeValid = (
  dateTimeRange: DatasetFilter["dateTimeRange"]
): boolean => {
  if (!dateTimeRange) return false;

  const isComplete = (part?: { date?: string; time?: string }) =>
    !!part?.date === !!part?.time;

  const isFromComplete = isComplete(dateTimeRange.from);
  const isToComplete = isComplete(dateTimeRange.to);

  const isAllEmpty =
    !dateTimeRange?.from?.date &&
    !dateTimeRange?.from?.time &&
    !dateTimeRange?.to?.date &&
    !dateTimeRange?.to?.time;

  return isAllEmpty || (isFromComplete && isToComplete);
};

export const generateBase64Filters = ({
  filters,
  timezone,
  h3Configuration
}: {
  filters: DatasetFilter[];
  timezone?: Timezone;
  h3Configuration?: H3Configuration | null;
}) => {
  if (!filters.length) return undefined;

  const filterTransformed = getDeepestAdminFilterFirst(filters);
  const filterMap: DatasetFilterPayload[] = mapDatasetFiltersToPayload(
    filterTransformed,
    timezone
  );

  return safeBase64Encode(
    JSON.stringify({
      filters: filterMap,
      ...buildGridConfig(h3Configuration)
    })
  );
};

export const getStreamUrlDatasetStream = ({
  filters,
  timezone,
  h3Configuration,
  datasetId
}: {
  filters?: DatasetFilter[];
  timezone?: Timezone;
  h3Configuration?: H3Configuration | null;
  datasetId?: string;
}) => {
  if (!datasetId) return "";

  const params = new URLSearchParams();
  const filter = generateBase64Filters({
    filters: filters || [],
    timezone,
    h3Configuration
  });

  if (filter) params.set("filter", filter);

  const queryString = params.toString() ? `?${params.toString()}` : "";

  return `${axiosInstance.defaults.baseURL}/v1/datasets/${datasetId}/data/stream${queryString}`;
};
